import { Author } from "api/author";
import { findUserByAssignee } from "../utils";
import { ReviewPull, ReviewRepo, ReviewRequest } from "./types";

const PULL_URL_PATTERN =
  /^https:\/\/github\.com\/KlydoClock\/([A-Za-z0-9_.-]+)\/pull\/(\d+)(?:[/?#].*)?$/i;

const REPO_BY_GITHUB_NAME: Record<string, ReviewRepo> = {
  "klydo-api": "api",
  backoffice: "admin",
  "klydo-studio": "studio",
  "klydo-functions": "functions",
  "klydo-shopify": "shopify",
  "clock-application-v3": "clock",
};

// `repo` is null for a KlydoClock PR url in a repo the UI does not know; the
// API owns the repo list and gives the final answer.
export type ParsedPullUrl = { repo: ReviewRepo | null; number: number };

// Client-side echo of the API rule, so the form can show the repo chip and
// block obvious mistakes before the request is sent. The API is the authority.
export function parsePullUrl(url: string): ParsedPullUrl | null {
  const match = PULL_URL_PATTERN.exec(url.trim());
  if (!match) return null;
  return {
    repo: REPO_BY_GITHUB_NAME[match[1].toLowerCase()] ?? null,
    number: parseInt(match[2], 10),
  };
}

export function hoursSince(iso: string, now: Date = new Date()): number {
  return Math.max(0, (now.getTime() - new Date(iso).getTime()) / 3600000);
}

export function formatWaiting(iso: string): string {
  const hours = hoursSince(iso);
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

// Colour for the "waiting" column: calm under 4h, warm under a day, red after.
export function waitingColor(iso: string): string {
  const hours = hoursSince(iso);
  if (hours < 4) return "text.secondary";
  if (hours < 24) return "warning.main";
  return "error.main";
}

export function pullLabel(pull: ReviewPull): string {
  return `#${pull.number}`;
}

export function userName(userId: string, users: Author[]): string {
  return findUserByAssignee(userId, users)?.name?.trim() || "Unknown";
}

export function isRequester(request: ReviewRequest, uid: string): boolean {
  return !!uid && request.requesterId === uid;
}

export function isReviewer(request: ReviewRequest, uid: string): boolean {
  return !!uid && request.reviewers.some((r) => r.userId === uid);
}

export function isTeamRequest(request: ReviewRequest): boolean {
  return request.audience === "channel";
}

export function slackThreadUrl(request: ReviewRequest): string | null {
  const thread = request.slackThread;
  if (!thread) return null;
  return `https://klydoclock.slack.com/archives/${thread.channel}/p${thread.ts.replace(".", "")}`;
}

export function hasNewCommits(request: ReviewRequest): boolean {
  return request.prs.some((pull) => pull.hasNewCommitsSinceRound);
}

export function approvedCount(request: ReviewRequest): number {
  return request.reviewers.filter((r) => r.status === "approved").length;
}
